import { Link } from "react-router-dom";
import { Star, Clock, Pause, Moon, MapPin, Bike, ShoppingBag } from "lucide-react";
import { useTenant } from "../contexts/TenantContext";
import { computeEtaMinutes, formatDeliveryEta } from "../lib/deliveryEta";

const STATE_PILL = {
    OPEN: { label: null, klass: "" },
    CLOSING_SOON: { label: "Closing soon", klass: "bg-amber-100 text-amber-800" },
    CLOSED: { label: "Closed", klass: "bg-neutral-200 text-neutral-700" },
    PAUSED: { label: "Paused", klass: "bg-red-100 text-red-700" },
};

export default function RestaurantCard({ r, compact }) {
    const { tenant } = useTenant();
    const hasImage = !!(r.cover_image_url || r.logo_url);
    const state = r.open_state || "OPEN";
    const closed = state === "CLOSED" || state === "PAUSED";
    const pill = STATE_PILL[state];
    // iter134q — tenant may hide the ETA pill or replace the per-shop
    // minutes with a literal string (e.g. "2 Days"). Legacy tenants
    // fall through to the pre-iter134q behaviour ("N mins").
    // BUG-FIX: ETA now factors in r.distance_km (already computed
    // server-side, shown right below as "X km away") instead of just
    // prep time — a 0km shop and a 4km shop no longer show identical ETAs.
    const etaText = formatDeliveryEta(tenant, computeEtaMinutes(r.avg_prep_minutes || 25, r.distance_km));

    return (
        <Link
            to={`/restaurant/${r.id}`}
            data-testid={`restaurant-card-${r.id}`}
            className={`block group active:scale-[0.98] transition ${closed ? "opacity-70" : ""}`}
        >
            {hasImage && (
                <div className={`relative overflow-hidden rounded-2xl bg-muted aspect-[4/3] ${closed ? "grayscale-[40%]" : ""}`}>
                    <img
                        src={r.cover_image_url || r.logo_url}
                        alt={r.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    />
                    {r.is_featured && state === "OPEN" && (
                        <span className="absolute top-2 left-2 bg-accent text-accent-foreground text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                            Featured
                        </span>
                    )}
                    {closed && (
                        <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
                            <span className="bg-white/95 text-foreground inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow">
                                {state === "PAUSED" ? <Pause size={12} /> : <Moon size={12} />} {r.status_label || (state === "PAUSED" ? "Paused" : "Closed")}
                            </span>
                        </div>
                    )}
                    {state === "CLOSING_SOON" && (
                        <span className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                            {r.status_label || "Closing soon"}
                        </span>
                    )}
                    <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2">
                        {etaText ? (
                            <span
                                data-testid={`restaurant-eta-${r.id}`}
                                className="bg-foreground/80 backdrop-blur-md text-background text-[11px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1"
                            >
                                <Clock size={11} /> {etaText}
                            </span>
                        ) : <span />}
                        {/* iter128 — Hide the rating pill entirely when no customer
                            has rated this shop yet. Previously we fell back to a
                            hard-coded "4.0" which mislead both merchants (who saw
                            a fake baseline) and customers. Show only if there's
                            a real rating (numeric) AND at least one rating count. */}
                        {(r.rating_count > 0 && typeof r.rating === "number") && (
                            <span className="bg-emerald-700 text-white text-[11px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1">
                                {r.rating.toFixed(1)} <Star size={10} className="fill-white" />
                            </span>
                        )}
                    </div>
                </div>
            )}
            <div className={hasImage ? "px-1 pt-2.5" : "p-3 bg-card border border-border rounded-2xl"}>
                <h3 className="font-semibold text-base text-foreground line-clamp-1">{r.name}</h3>
                <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{(r.cuisines || []).join(" • ")}</p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {typeof r.distance_km === "number" && (
                        <span className="text-[11px] font-semibold text-muted-foreground inline-flex items-center gap-1" data-testid={`distance-${r.id}`}>
                            <MapPin size={10} /> {r.distance_km.toFixed(1)} km away
                        </span>
                    )}
                    {r.home_delivery_enabled === false && r.pickup_enabled !== false && (
                        <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"><ShoppingBag size={9} /> Pickup only</span>
                    )}
                    {r.pickup_enabled === false && r.home_delivery_enabled !== false && (
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"><Bike size={9} /> Delivery only</span>
                    )}
                    {pill?.label && (
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${pill.klass}`}>{r.status_label || pill.label}</span>
                    )}
                    {!hasImage && (
                        <>
                            {r.is_featured && state === "OPEN" && (
                                <span className="bg-accent text-accent-foreground text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">Featured</span>
                            )}
                            {etaText && (
                                <span className="text-[11px] font-semibold text-muted-foreground inline-flex items-center gap-1">
                                    <Clock size={10} /> {etaText}
                                </span>
                            )}
                            {/* Same iter128 guard as the hero pill above — no fake baseline. */}
                            {(r.rating_count > 0 && typeof r.rating === "number") && (
                                <span className="bg-emerald-700 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                    {r.rating.toFixed(1)} <Star size={9} className="fill-white" />
                                </span>
                            )}
                        </>
                    )}
                </div>
                {hasImage && !compact && r.min_order_value > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-1">Min order ₹{r.min_order_value}</p>
                )}
            </div>
        </Link>
    );
}
